// Reproductor de efectos de sonido (SFX) y pasos (ambient).
// Recibe IDs del server vía el packet handler playSound (engine/connection/messages.js).
//
// Los archivos viven en public/static/sounds/sfx/<id>.{ogg,wav}.
//
// Optimizaciones aplicadas (audit Tier 3 #2):
//   1) Settings CACHEADOS en memoria — antes cada play()/playSteps() hacía
//      2-3 localStorage.getItem(), bloqueante en main thread (~0.2-1ms idle,
//      hasta 5ms bajo presión I/O). Multiplicado por ~25 SFX/seg en zona
//      poblada eran ~50ms/seg de jank. Ahora se lee 1 sola vez al boot
//      + cada que el user cambia un slider (refreshSettings()).
//   2) Pool de Audio elements pre-creados — antes cada play() hacía
//      `new Audio(url)` que aloca HTMLAudioElement + inicializa el audio
//      pipeline interno + setea event listeners. 25/seg → presión de
//      major-GC cada 5-10s. Ahora reusamos 3 elements por idSound;
//      el primer .play() del idSound los crea, después solo
//      `currentTime = 0` + `.play()`.

const POOL_SIZE = 3; // 3 elements por idSound (cubre overlap con throttle 60ms)

// IDs de sounds cuyo `.ogg` del cliente original VB6 no se decodea en
// Chrome (Vorbis legacy malformado). Usamos `.wav` (copiados de
// _reference/ao-recursos/wav/). Mismo workaround que el de pasos.
// Agregar IDs nuevos a este Set cuando aparezca otro SFX "silencioso".
const WAV_IDS = new Set([2 /* SND_SWING — miss en cuerpo a cuerpo */]);

// Per-sound volume override (multiplier sobre `_effectsVolume`).
// 0.7 = 70% del efecto base, 1.0 = sin cambio, 0.0 = mute.
// Útil cuando un .ogg fue masterizado más fuerte que el resto y rompe el
// balance. Ej: el grito 100 (Furia de Uhkrul) está al 70% para que no
// sature contra los SFX de melee/spells del entorno.
const SOUND_VOLUME_OVERRIDES = {
    100: 0.7,
    46: 0.7   // SND_BEBER — sonido de potear (pociones rojas, azules, etc.)
};

class AudioManager {
    constructor() {
        this.lastPlayedAt = {};
        this.lastStepAt = 0;
        this.stepToggle = false;

        // Cache de settings. Inicializada vía refreshSettings(),
        // llamada también desde el modal de audio cuando cambian sliders.
        this._musicEnabled = true;
        this._effectsVolume = 0.7;
        this._ambienceVolume = 0.5;

        // Pool de Audio elements. Key: "{idSound}.{ext}", value: array de
        // Audio elements precargados. Lazy — el primer play() de un sound
        // los crea, después se reusan.
        this.audioPool = {};

        // Settings se lee de localStorage la primera vez sincrónico (acá
        // todavía no estamos en el render path), después solo en
        // refreshSettings() explícito.
        if (typeof window !== "undefined") {
            this.refreshSettings();
        }
    }

    // Re-lee settings de localStorage y los cachea. Llamar:
    //   - 1 vez al boot (lo hace el constructor)
    //   - Cada vez que el user cambia un slider en el modal de audio
    //     (wired desde pages/play/index.js)
    refreshSettings() {
        try {
            this._musicEnabled =
                localStorage.getItem("musicEnabled") !== "false";

            const ef = Number(localStorage.getItem("effectsVolume"));
            this._effectsVolume = Number.isFinite(ef)
                ? Math.max(0, Math.min(1, ef / 100))
                : 0.7;

            const am = Number(localStorage.getItem("ambienceVolume"));
            this._ambienceVolume = Number.isFinite(am)
                ? Math.max(0, Math.min(1, am / 100))
                : 0.5;
        } catch (e) {
            // SSR o localStorage bloqueado — defaults razonables.
            this._musicEnabled = true;
            this._effectsVolume = 0.7;
            this._ambienceVolume = 0.5;
        }
    }

    // Saca un Audio element del pool para `idSound`. ext típicamente "ogg"
    // (SFX) o "wav" (pasos). Lazy-crea la pool de 3 elements la primera
    // vez. Si todos están busy (ej. solapamiento rápido más allá del
    // throttle), retorna null para que el caller alloque ad-hoc — caso
    // edge, no afecta el path común.
    getPooledAudio(idSound, ext) {
        const key = `${idSound}.${ext}`;
        let pool = this.audioPool[key];
        if (!pool) {
            pool = this.audioPool[key] = [];
            const url = `/static/sounds/sfx/${idSound}.${ext}`;
            for (let i = 0; i < POOL_SIZE; i++) {
                // preload="auto" para que el browser baje el archivo ya y
                // tengamos decode ready. Sin esto, el primer .play() de
                // cada element tiene un pequeño delay de fetch+decode.
                const a = new Audio(url);
                a.preload = "auto";
                pool.push(a);
            }
        }

        // Buscar uno que esté libre (ended o paused tras play).
        for (let i = 0; i < pool.length; i++) {
            const a = pool[i];
            if (a.ended || a.paused) {
                return a;
            }
        }
        // Todos busy — fallback null.
        return null;
    }

    play(idSound) {
        try {
            if (!idSound || typeof window === "undefined") return;
            if (!this._musicEnabled) return;
            if (this._effectsVolume <= 0) return;

            // Throttle: evita spammear el mismo sonido más de N veces por seg.
            const now = Date.now();
            const last = this.lastPlayedAt[idSound] || 0;
            if (now - last < 60) return;
            this.lastPlayedAt[idSound] = now;

            // WAV para IDs conocidos como OGG-roto (Vorbis legacy del
            // cliente VB6 que Chrome no decodea). Por default OGG.
            const ext = WAV_IDS.has(idSound) ? "wav" : "ogg";
            const pooled = this.getPooledAudio(idSound, ext);
            const audio =
                pooled || new Audio(`/static/sounds/sfx/${idSound}.${ext}`);
            const volOverride = SOUND_VOLUME_OVERRIDES[idSound] || 1;
            audio.volume = this._effectsVolume * volOverride;
            if (pooled) audio.currentTime = 0;
            audio.play().catch(() => {});
        } catch (err) {
            console.log("AudioManager.play error:", err && err.message);
        }
    }

    // Sonido de pasos al caminar. Alterna 23.wav / 24.wav (los del cliente
    // original — copiados desde _reference/ao-recursos/wav/). Usa WAV en
    // lugar de OGG porque los .ogg del cliente fallan en Chrome con
    // "NotSupportedError: Failed to load because no supported source was
    // found" — los archivos son Vorbis válidos pero Chrome no los decodea.
    // Los WAV (PCM 16bit mono 11025Hz) andan en todos los browsers sin drama.
    // El volumen usa el slider "ambient" para ser independiente de SFX.
    //
    // `idPj` opcional: si se pasa, throttle per-char (180ms entre pasos
    // del mismo char remoto). Sin idPj, throttle global del local user
    // (lastStepAt). Separados para que ambos caminos no se choquen.
    playSteps(idPj) {
        try {
            if (typeof window === "undefined") return;
            if (!this._musicEnabled) return;
            if (this._ambienceVolume <= 0) return;

            const now = Date.now();
            if (idPj != null) {
                // Throttle per-char remoto. Diccionario propio para no
                // pisar el lastStepAt del local user.
                if (!this.lastStepAtRemote) this.lastStepAtRemote = {};
                if (now - (this.lastStepAtRemote[idPj] || 0) < 180) return;
                this.lastStepAtRemote[idPj] = now;
            } else {
                // Throttle global del local user.
                if (now - this.lastStepAt < 180) return;
                this.lastStepAt = now;
            }

            this.stepToggle = !this.stepToggle;
            const idSound = this.stepToggle ? 23 : 24;

            const pooled = this.getPooledAudio(idSound, "wav");
            const audio = pooled || new Audio(`/static/sounds/sfx/${idSound}.wav`);
            audio.volume = this._ambienceVolume;
            if (pooled) audio.currentTime = 0;
            audio.play().catch(() => {});
        } catch (err) {
            console.log("AudioManager.playSteps error:", err && err.message);
        }
    }

    // ── Compat ─────────────────────────────────────────────────────────
    // Métodos viejos `isEnabled()` / `getEffectsVolume()` / `getAmbienceVolume()`
    // eliminados porque ahora todo el state vive en `this._*` y se accede
    // directo. Si algún código fuera del módulo los llamaba, romperíamos
    // ese site (debería no haber callers externos — el AudioManager se
    // accede solo vía play/playSteps).
}

const audioManager = new AudioManager();

export default audioManager;
